import { Tooltip } from '@material-ui/core'
import Icon from '@material-ui/core/Icon'
import IconButton from '@material-ui/core/IconButton'
import { MUIDataTableColumnDef } from 'mui-datatables'
import React, { Fragment, useState } from 'react'
import { EnumUser } from '../../../../../graphql/typings/global_types'
import DataTable from '../../../../components/DataTable'
import { ConfirmDialog } from '../../../../components/Dialog'
import Panel from '../../../../components/Panel'
import { Broker_Minimal } from '../../../../helpers/data/models/contract/Broker'
import { useErrorService } from '../../../../helpers/errors/ErrorService'
import { useAuthUser } from '../../../../hooks/useAuth'
import { useCommonDataLoadedExisting } from '../../../../hooks/useCommonData'
import { useDeleteBroker } from '../../../../hooks/useMutations'
import useSnackBar from '../../../../hooks/useSnackBar'
import BrokerDialog from './BrokerDialog'

type DialogState =
    | {
          open: true
          broker: Broker_Minimal
      }
    | { open: false }

const AEABrokersList = () => {
    const { brokers } = useCommonDataLoadedExisting()
    const { errorAlert } = useErrorService()
    const auth = useAuthUser()
    const isAdmin = auth.user.userType === EnumUser.admin
    const { openSnack } = useSnackBar()
    const deleteBrokerMutation = useDeleteBroker()

    const [openAddDialog, setOpenAddDialog] = useState<boolean>(false)
    const [openEditDialog, setOpenEditDialog] = useState<DialogState>({ open: false })
    const [openDeleteDialog, setOpenDeleteDialog] = useState<DialogState>({
        open: false,
    })
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false)

    const columns: MUIDataTableColumnDef[] = [
        {
            name: 'title',
            label: 'Nom',
        },
        {
            name: 'phone',
            label: 'Téléphone',
        },
        {
            name: 'email',
            label: 'Email',
        },
    ]

    const addFeatureData = {
        addHandler: () => {
            setOpenAddDialog(true)
        },
        tooltip: `Ajout d'un courtier`,
    }

    const onCreated = (broker: Broker_Minimal) => {
        openSnack({
            type: 'success',
            message: `Courtier ${broker.title} créé avec succès !`,
        })
    }

    const onEdited = (broker: Broker_Minimal) => {
        openSnack({
            message: 'Courtier modifié avec succès !',
            type: 'success',
        })
    }

    const onEditClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, broker: Broker_Minimal) => {
        event.stopPropagation()
        setOpenEditDialog({ open: true, broker })
    }

    const onDeleteClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, broker: Broker_Minimal) => {
        event.stopPropagation()
        setOpenDeleteDialog({ open: true, broker })
    }

    const onDeleteConfirmed = async () => {
        if (!openDeleteDialog.open) return

        setDeleteLoading(true)

        try {
            const broker = openDeleteDialog.broker

            const response = await deleteBrokerMutation.run({
                id: broker.id,
            })

            if (response.errors.length > 0) {
                throw 'Une erreur est survenue pendant la suppression du courtier.'
            } else {
                openSnack({
                    message: 'Courtier supprimé avec succès !',
                    type: 'success',
                })
            }
        } catch (error) {
            errorAlert(error)
        } finally {
            setDeleteLoading(false)
            setOpenDeleteDialog({ open: false })
        }
    }

    const actionsColumn = (dataIndex: number, rowIndex: number) => {
        if (!isAdmin) return null

        const broker = brokers[dataIndex]

        return (
            <div className="flex items-center">
                <div className="flex-grow"></div>
                <Tooltip title="Modifier le courtier">
                    <IconButton onClick={(event) => onEditClick(event, broker)}>
                        <Icon>edit</Icon>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer le courtier">
                    <IconButton onClick={(event) => onDeleteClick(event, broker)}>
                        <Icon>delete</Icon>
                    </IconButton>
                </Tooltip>
            </div>
        )
    }

    return (
        <Fragment>
            <Panel>
                <DataTable
                    title={'Courtiers'}
                    data={brokers}
                    columns={columns}
                    showSearch={true}
                    actionsColumn={actionsColumn}
                    addFeature={addFeatureData}
                    options={{
                        sortOrder: {
                            name: 'title',
                            direction: 'asc',
                        },
                    }}
                />
            </Panel>
            <BrokerDialog onDone={onCreated} open={openAddDialog} closeDialog={() => setOpenAddDialog(false)} />
            {openEditDialog.open && (
                <BrokerDialog
                    onDone={onEdited}
                    open={openEditDialog.open}
                    closeDialog={() => setOpenEditDialog({ open: false })}
                    broker={openEditDialog.broker}
                />
            )}
            {openDeleteDialog.open && (
                <ConfirmDialog
                    open={openDeleteDialog.open}
                    onCancelled={() => setOpenDeleteDialog({ open: false })}
                    onConfirmed={onDeleteConfirmed}
                    title="Suppression d'un courtier"
                    text={`Voulez-vous vraiment supprimer le courtier "${openDeleteDialog.broker.title}" ?`}
                    loading={deleteLoading}
                />
            )}
        </Fragment>
    )
}

export default AEABrokersList
